import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ArrowLeft, Upload, Sparkles, ImagePlus, Send } from "lucide-react";


interface NewsPost {
  id: string;
  title_es: string;
  title_en: string;
  title_it: string;
  description_es: string;
  description_en: string;
  description_it: string;
  image_url: string | null;
  created_at: string;
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<NewsPost | null>(null);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title_es: "", title_en: "", title_it: "",
    description_es: "", description_en: "", description_it: "",
    image_url: "" as string | null,
  });

  // AI assistant state
  type ChatMsg = { role: "user" | "assistant"; text: string };
  const [aiMessages, setAiMessages] = useState<ChatMsg[]>([
    { role: "assistant", text: "Hola 👋 Sube una foto y dime brevemente qué quieres anunciar. Te propondré un título y descripción en español." },
  ]);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiImage, setAiImage] = useState<{ dataUrl: string; file: File } | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<{ title: string; description: string; imageDataUrl?: string; file?: File } | null>(null);

  const verifyAdmin = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { navigate("/admin"); return null; }
    const { data, error } = await supabase.rpc("has_role", { _user_id: session.user.id, _role: "admin" });
    if (error || !data) {
      toast.error("Acceso denegado: se requiere rol de administrador");
      await supabase.auth.signOut();
      navigate("/admin");
      return null;
    }
    return session;
  };

  useEffect(() => {
    const check = async () => {
      const session = await verifyAdmin();
      if (session) fetchPosts();
    };
    check();
  }, [navigate]);

  const fetchPosts = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("news_posts").select("*").order("created_at", { ascending: false });
    if (error) toast.error("Error cargando novedades");
    else setPosts(data || []);
    setLoading(false);
  };

  const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"] as const;
  const EXT_BY_MIME: Record<string, string> = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
  };

  const handleImageUpload = async (file: File) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type as typeof ALLOWED_IMAGE_TYPES[number])) {
      toast.error("Formato no permitido. Usa JPG, PNG, WEBP o GIF.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen supera el límite de 5MB.");
      return;
    }
    setUploading(true);
    const ext = EXT_BY_MIME[file.type];
    const path = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("news-images").upload(path, file, {
      contentType: file.type,
      upsert: false,
    });
    if (error) { toast.error("Error subiendo imagen"); setUploading(false); return; }
    const { data: urlData } = supabase.storage.from("news-images").getPublicUrl(path);
    setForm({ ...form, image_url: urlData.publicUrl });
    setUploading(false);
    toast.success("Imagen subida");
  };

  const handleSave = async () => {
    const session = await verifyAdmin();
    if (!session) return;

    if (editing) {
      const { error } = await supabase.from("news_posts").update({
        title_es: form.title_es, title_en: form.title_en, title_it: form.title_it,
        description_es: form.description_es, description_en: form.description_en, description_it: form.description_it,
        image_url: form.image_url,
      }).eq("id", editing.id);
      if (error) toast.error("Error actualizando");
      else { toast.success("Novedad actualizada"); setEditing(null); fetchPosts(); }
    } else {
      const { error } = await supabase.from("news_posts").insert({
        title_es: form.title_es, title_en: form.title_en, title_it: form.title_it,
        description_es: form.description_es, description_en: form.description_en, description_it: form.description_it,
        image_url: form.image_url, user_id: session.user.id,
      });
      if (error) toast.error("Error creando novedad");
      else { toast.success("Novedad creada"); setCreating(false); fetchPosts(); }
    }
    resetForm();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Seguro que quieres eliminar esta novedad?")) return;
    const session = await verifyAdmin();
    if (!session) return;
    const { error } = await supabase.from("news_posts").delete().eq("id", id);
    if (error) toast.error("Error eliminando");
    else { toast.success("Novedad eliminada"); fetchPosts(); }
  };

  const startEdit = (post: NewsPost) => {
    setEditing(post);
    setCreating(false);
    setForm({
      title_es: post.title_es, title_en: post.title_en, title_it: post.title_it,
      description_es: post.description_es, description_en: post.description_en, description_it: post.description_it,
      image_url: post.image_url,
    });
  };

  const resetForm = () => {
    setForm({ title_es: "", title_en: "", title_it: "", description_es: "", description_en: "", description_it: "", image_url: null });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin");
  };

  const handleAiImagePick = (file: File) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type as typeof ALLOWED_IMAGE_TYPES[number])) {
      toast.error("Formato no permitido. Usa JPG, PNG, WEBP o GIF.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen supera el límite de 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAiImage({ dataUrl: reader.result as string, file });
    reader.readAsDataURL(file);
  };

  const handleAiSend = async () => {
    if (!aiPrompt.trim() && !aiImage) {
      toast.error("Escribe una indicación o sube una foto.");
      return;
    }
    const session = await verifyAdmin();
    if (!session) return;

    const userText = aiPrompt.trim() || "(solo imagen adjunta)";
    setAiMessages((m) => [...m, { role: "user", text: userText + (aiImage ? " 📷" : "") }]);
    setAiLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("generate-news", {
        body: { prompt: aiPrompt.trim() || "Genera una novedad basada en la imagen adjunta.", imageDataUrl: aiImage?.dataUrl },
      });
      if (error) throw error;
      if (!data?.title && !data?.description) throw new Error("Respuesta vacía");

      setAiSuggestion({
        title: data.title || "",
        description: data.description || "",
        imageDataUrl: aiImage?.dataUrl,
        file: aiImage?.file,
      });
      setAiMessages((m) => [...m, { role: "assistant", text: `📝 ${data.title}\n\n${data.description}` }]);
      setAiPrompt("");
      setAiImage(null);
    } catch (e: any) {
      console.error(e);
      const msg = e?.message || "Error generando contenido";
      toast.error(msg);
      setAiMessages((m) => [...m, { role: "assistant", text: "Lo siento, no pude generar la propuesta. Inténtalo de nuevo." }]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleUseAiSuggestion = async () => {
    if (!aiSuggestion) return;
    let imageUrl: string | null = null;
    if (aiSuggestion.file) {
      const session = await verifyAdmin();
      if (!session) return;
      setUploading(true);
      const ext = EXT_BY_MIME[aiSuggestion.file.type] || "jpg";
      const path = `${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("news-images").upload(path, aiSuggestion.file, {
        contentType: aiSuggestion.file.type,
        upsert: false,
      });
      setUploading(false);
      if (error) { toast.error("Error subiendo imagen"); return; }
      imageUrl = supabase.storage.from("news-images").getPublicUrl(path).data.publicUrl;
    }
    setCreating(true);
    setEditing(null);
    setForm({
      title_es: aiSuggestion.title,
      title_en: "",
      title_it: "",
      description_es: aiSuggestion.description,
      description_en: "",
      description_it: "",
      image_url: imageUrl,
    });
    setAiSuggestion(null);
    toast.success("Propuesta aplicada al formulario. Revisa y guarda.");
  };

  const showForm = creating || editing;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Gestión de Novedades | Panel | Federico Duronio</title>
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href="https://federicoduronio.com/admin/dashboard" />
      </Helmet>
      <header className="bg-card border-b border-border p-4">
        <div className="container mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold text-foreground">Gestión de Novedades del sitio</h1>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/home")} aria-label="Volver a la web pública">
              <ArrowLeft className="w-4 h-4 mr-1" /> Web
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} aria-label="Cerrar sesión del panel">
              <LogOut className="w-4 h-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>


      <main className="container mx-auto p-4 max-w-4xl">
        {!showForm && (
          <>
            {/* AI Assistant */}
            <Card className="p-5 mb-6 border-primary/30 bg-gradient-to-br from-card to-primary/5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold">Asistente IA · Novedades</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Sube una foto y describe la novedad. La IA propondrá un título y descripción listos para revisar.
              </p>

              <div className="bg-background/60 rounded-md border border-border p-3 max-h-72 overflow-y-auto space-y-3 mb-3">
                {aiMessages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                      m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                    }`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {aiLoading && (
                  <div className="flex justify-start">
                    <div className="bg-muted text-muted-foreground rounded-lg px-3 py-2 text-sm">Generando…</div>
                  </div>
                )}
              </div>

              {aiSuggestion && (
                <div className="mb-3 p-3 rounded-md border border-primary/40 bg-primary/5 flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-sm text-foreground">Propuesta lista. ¿La aplicamos al formulario?</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="premium" onClick={handleUseAiSuggestion} disabled={uploading}>
                      {uploading ? "Subiendo…" : "Usar en formulario"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setAiSuggestion(null)}>Descartar</Button>
                  </div>
                </div>
              )}

              {aiImage && (
                <div className="mb-3 flex items-center gap-3 p-2 rounded-md border border-border bg-background/60">
                  <img src={aiImage.dataUrl} alt="Adjunta" className="w-14 h-14 rounded object-cover" />
                  <span className="text-xs text-muted-foreground flex-1 truncate">Foto adjunta</span>
                  <Button size="sm" variant="ghost" onClick={() => setAiImage(null)}>Quitar</Button>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2">
                <label className="cursor-pointer">
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleAiImagePick(e.target.files[0])} />
                  <Button type="button" variant="outline" size="sm" asChild>
                    <span><ImagePlus className="w-4 h-4 mr-1" /> Foto</span>
                  </Button>
                </label>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Ej: Nuevo curso de latte art en marzo, 4 plazas, cafetería Madrid…"
                  rows={2}
                  className="flex-1"
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleAiSend(); }}
                />
                <Button variant="premium" size="sm" onClick={handleAiSend} disabled={aiLoading}>
                  <Send className="w-4 h-4 mr-1" /> Enviar
                </Button>
              </div>
            </Card>

            <Button variant="premium" className="mb-6" onClick={() => { setCreating(true); setEditing(null); resetForm(); }}>
              <Plus className="w-4 h-4 mr-1" /> Nueva Novedad
            </Button>
          </>
        )}

        {showForm && (
          <Card className="p-6 mb-6 space-y-4">
            <h2 className="text-lg font-bold">{editing ? "Editar Novedad" : "Nueva Novedad"}</h2>

            {/* Image upload */}
            <div className="space-y-2">
              <Label>Imagen</Label>
              {form.image_url && (
                <img src={form.image_url} alt="Preview" className="w-full max-h-48 object-cover rounded-md" />
              )}
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])} />
                  <Button type="button" variant="outline" size="sm" asChild disabled={uploading}>
                    <span><Upload className="w-4 h-4 mr-1" />{uploading ? "Subiendo..." : "Subir imagen"}</span>
                  </Button>
                </label>
              </div>
            </div>

            {/* Titles */}
            {(["es", "en", "it"] as const).map((lang) => (
              <div key={`title_${lang}`} className="space-y-1">
                <Label>Título ({lang.toUpperCase()})</Label>
                <Input value={form[`title_${lang}`]} onChange={(e) => setForm({ ...form, [`title_${lang}`]: e.target.value })} />
              </div>
            ))}

            {/* Descriptions */}
            {(["es", "en", "it"] as const).map((lang) => (
              <div key={`desc_${lang}`} className="space-y-1">
                <Label>Descripción ({lang.toUpperCase()})</Label>
                <Textarea value={form[`description_${lang}`]} onChange={(e) => setForm({ ...form, [`description_${lang}`]: e.target.value })} rows={3} />
              </div>
            ))}

            <div className="flex gap-2">
              <Button variant="premium" onClick={handleSave}>Guardar</Button>
              <Button variant="outline" onClick={() => { setCreating(false); setEditing(null); resetForm(); }}>Cancelar</Button>
            </div>
          </Card>
        )}

        {loading ? (
          <p className="text-muted-foreground">Cargando...</p>
        ) : posts.length === 0 ? (
          <p className="text-muted-foreground">No hay novedades todavía. ¡Crea la primera!</p>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <Card key={post.id} className="p-4 flex gap-4 items-start">
                {post.image_url && (
                  <img src={post.image_url} alt={post.title_es} className="w-24 h-24 rounded-md object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-foreground truncate">{post.title_es}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{post.description_es}</p>
                  <p className="text-xs text-muted-foreground mt-1">{new Date(post.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => startEdit(post)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(post.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
